export default function FinalCTA() {
  return (
    <section className="relative overflow-hidden py-16 md:py-28">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0B0F19]" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/40 via-[#0B0F19] to-orange-900/30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-[#7C3AED]/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-orange-500/15 rounded-full blur-[100px] pointer-events-none" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
        {/* Urgency badge */}
        <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 text-orange-300 text-xs font-bold px-4 py-2 rounded-full mb-8 animate-pulse">
          <i className="ri-time-line"></i>
          Precio de lanzamiento por tiempo limitado
        </div>

        {/* Main headline */}
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-[1.05] mb-6">
          El mercado no perdona{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
            a los lentos.
          </span>
          <br />
          <span className="text-white/80 text-2xl md:text-4xl lg:text-5xl">
            ¿Te subes o te quedas atrás?
          </span>
        </h2>

        <p className="text-white/60 text-sm md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
          Mientras lees esto, tu competencia ya está usando herramientas de IA para quitarte clientes.
          El momento de actuar es{" "}
          <strong className="text-white">ahora</strong>.
        </p>

        {/* Price display */}
        <div className="flex items-center justify-center mb-8">
          <div className="text-center">
            <p className="text-white/30 text-base md:text-lg line-through">S/ 97</p>
            <p className="text-6xl md:text-7xl font-black text-white">S/ 15</p>
            <p className="text-white/40 text-xs md:text-sm mt-1">Pago único. Sin mensualidades. Sin sorpresas.</p>
          </div>
        </div>

        {/* Device compatibility */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          {[
            { icon: "ri-smartphone-line", text: "iOS & Android" },
            { icon: "ri-tablet-line", text: "Tablet" },
            { icon: "ri-computer-line", text: "PC / Mac" },
            { icon: "ri-lock-password-line", text: "Acceso Privado" },
          ].map((d, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-white/8 border border-white/15 px-3 py-1.5 rounded-full">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${d.icon} text-[#a78bfa] text-sm`}></i>
              </div>
              <span className="text-white/70 text-xs font-semibold">{d.text}</span>
            </div>
          ))}
        </div>

        {/* Private access info */}
        <div className="bg-white/5 border border-[#7C3AED]/30 rounded-xl px-4 py-3 mb-8 max-w-sm mx-auto">
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-lock-fill text-[#a78bfa] text-base"></i>
            </div>
            <p className="text-white/70 text-xs md:text-sm">
              Acceso <strong className="text-white">100% privado</strong> con usuario y contraseña personal
            </p>
          </div>
        </div>

        {/* CTA Button — split into two lines on mobile */}
        <div className="flex flex-col items-center gap-3">
          <a
            href="#"
            className="inline-flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 hover:from-yellow-400 hover:via-orange-400 hover:to-orange-500 text-white font-black text-base md:text-xl px-8 md:px-14 py-4 md:py-6 rounded-2xl sm:rounded-full transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50 cursor-pointer group w-full sm:w-auto max-w-xs sm:max-w-none"
          >
            <span className="flex items-center gap-2">
              <i className="ri-rocket-line text-xl group-hover:rotate-12 transition-transform duration-300"></i>
              ENTRAR AL ECOSISTEMA
            </span>
            <span className="flex items-center gap-2">
              HOY POR S/ 15
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
            </span>
          </a>

          <p className="text-white/35 text-xs md:text-sm text-center">
            Acceso inmediato &bull; Pago 100% seguro &bull; Garantía 7 días
          </p>
        </div>

        {/* Security badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 md:gap-8">
          {[
            { icon: "ri-shield-check-fill", text: "SSL Seguro" },
            { icon: "ri-bank-card-fill", text: "Visa / Mastercard" },
            { icon: "ri-refresh-line", text: "Garantía 7 días" },
            { icon: "ri-customer-service-2-fill", text: "Soporte 24/7" },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 text-white/40">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${b.icon} text-emerald-400 text-sm`}></i>
              </div>
              <span className="text-xs font-medium">{b.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
